import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../shared/services/baseQuery';

import {
  type CreateServiceIdentityProps,
  type GetApplicationListProps,
  type GetIdentityListProps,
  type GetNameListProps,
  type GetOwnersListProps,
  type GetPoliciesListProps,
  type GetServiceAccountListProps,
  type GetServiceIdentityHistoryProps,
  type RotatePasswordProps,
  ServiceIdentityType,
  type UpdateOwnersListProps,
  type UpdateServiceAccountListProps,
  type UpdateServiceIdentityProps,
} from './serviceIdentityType';

/** RTK Query API slice for service identity CRUD, account linking, owner management, and credential rotation. */
const serviceIdentityApi = createApi({
  reducerPath: ServiceIdentityType.EP_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  endpoints: () => ({}),
});

/** Extended endpoints for identity list, details, history, disable/enable, create, update, rotate password, service accounts, and owners. */
export const serviceIdentityEndPoints = serviceIdentityApi.injectEndpoints({
  endpoints: (builder) => ({
    getIdentityList: builder.mutation<unknown, GetIdentityListProps>({
      query: (payload) => ({
        url: '/api/service/identities/list',
        method: 'POST',
        data: payload,
      }),
    }),
    getServiceIdDetails: builder.query<unknown, number>({
      query: (serviceId) => ({
        url: `/api/service/identities/${serviceId}`,
      }),
    }),
    getIdentityHistory: builder.mutation<
      unknown,
      GetServiceIdentityHistoryProps
    >({
      query: (payload) => ({
        url: '/api/service/identities/history',
        method: 'POST',
        data: {
          id: payload.id,
          cursor: payload.cursor,
          max: 5,
        },
      }),
    }),
    disableServiceId: builder.mutation<
      unknown,
      {
        serviceId: number;
        action: string;
      }
    >({
      query: (payload) => ({
        url: '/api/service/identities/accounts/action',
        method: 'POST',
        data: payload,
      }),
    }),
    createServiceIdentity: builder.mutation<
      unknown,
      CreateServiceIdentityProps
    >({
      query: (payload) => ({
        url: '/api/service/identities',
        method: 'POST',
        data: payload,
      }),
    }),
    getOwnersList: builder.mutation<unknown, GetOwnersListProps>({
      query: (payload) => ({
        url: '/api/service/identities/owners/list',
        method: 'POST',
        data: payload,
      }),
    }),
    getPolicyList: builder.mutation<unknown, GetPoliciesListProps>({
      query: (payload) => ({
        url: '/api/service/identities/policies/list',
        method: 'POST',
        data: payload,
      }),
    }),
    updateServiceIdentity: builder.mutation<
      unknown,
      UpdateServiceIdentityProps
    >({
      query: (payload) => ({
        url: `/api/service/identities/${payload.serviceId}`,
        method: 'PUT',
        data: payload.data,
      }),
    }),
    rotatePassword: builder.mutation<unknown, RotatePasswordProps>({
      query: (payload) => ({
        url: '/api/service/identities/accounts/password',
        method: 'PUT',
        data: payload,
      }),
    }),
    getServiceAccountList: builder.mutation<
      unknown,
      GetServiceAccountListProps
    >({
      query: (payload) => ({
        url: '/api/service/identities/accounts/list',
        method: 'POST',
        data: payload,
      }),
    }),
    updateServiceAccountList: builder.mutation<
      unknown,
      UpdateServiceAccountListProps
    >({
      query: (payload) => ({
        url: '/api/service/identities/accounts',
        method: 'PUT',
        data: payload,
      }),
    }),
    updateOwnersList: builder.mutation<unknown, UpdateOwnersListProps>({
      query: (payload) => ({
        url: '/api/service/identities/owners',
        method: 'PUT',
        data: payload,
      }),
    }),
    getNameList: builder.mutation<unknown, GetNameListProps>({
      query: (payload) => ({
        url: '/api/service/identities/filter',
        method: 'POST',
        data: payload,
      }),
    }),
    getApplicationList: builder.query<unknown, GetApplicationListProps>({
      query: (data) => ({
        url: '/api/v5/requestmcp/getEndpointsForRequest',
        params: { ...data },
      }),
    }),
  }),
});

export const {
  useLazyGetServiceIdDetailsQuery,
  useGetIdentityHistoryMutation,
  useGetIdentityListMutation,
  useDisableServiceIdMutation,
  useCreateServiceIdentityMutation,
  useGetOwnersListMutation,
  useGetPolicyListMutation,
  useUpdateServiceIdentityMutation,
  useRotatePasswordMutation,
  useGetServiceAccountListMutation,
  useUpdateServiceAccountListMutation,
  useUpdateOwnersListMutation,
  useGetNameListMutation,
  useLazyGetApplicationListQuery,
} = serviceIdentityEndPoints;

export default serviceIdentityApi;
